/**
 * The bootstrap wizard (2.J deliverable 11, screen `bootstrap.wizard`): four independent steps
 * under UI r3's Stepper, with Back and Next that only move. Only the current step is drawn; what
 * each holds is kept for the visit (`drafts.ts`). Shows a turned-off line with `flag:sessions` off.
 */
import { flagsFromMe } from '@codex/contracts';
import { Button, Stepper } from '@codex/ui';
import { useRef, useState } from 'react';
import { useShellSession } from '../shell/session.js';
import { Drafts, DraftsContext } from './drafts.js';
import {
  STEP_IDS,
  STEP_LABELS,
  type StepId,
  stepAfter,
  stepBefore,
  stepStatuses,
} from './model/wizard.js';
import { LedgerStep } from './steps/ledger-step.js';
import { NowStep } from './steps/now-step.js';
import { SessionsStep } from './steps/sessions-step.js';
import { StatusStep } from './steps/status-step.js';
import './bootstrap.css';

export const TURNED_OFF_COPY = 'The bootstrap wizard is turned off.';
export const TITLE = 'Bootstrap past sessions';

export function useBootstrapEnabled(): boolean {
  const me = useShellSession();
  return me !== null && flagsFromMe(me).sessions === true;
}

const STEPS: Readonly<Record<StepId, () => React.JSX.Element>> = {
  sessions: SessionsStep,
  now: NowStep,
  status: StatusStep,
  ledger: LedgerStep,
};

export function BootstrapWizard(): React.JSX.Element {
  const enabled = useBootstrapEnabled();
  if (!enabled) {
    return (
      <main className="cx-bootstrap">
        <h1>{TITLE}</h1>
        <p>{TURNED_OFF_COPY}</p>
      </main>
    );
  }
  return <Wizard />;
}

function Wizard(): React.JSX.Element {
  const drafts = useRef<Drafts>(new Drafts());
  const [current, setCurrent] = useState<StepId>('sessions');
  const [visited, setVisited] = useState<ReadonlySet<StepId>>(new Set(['sessions']));
  const go = (id: StepId | null) => {
    if (id === null) return;
    setCurrent(id);
    setVisited(new Set([...visited, id]));
  };
  const statuses = stepStatuses(current, visited);
  const Step = STEPS[current];
  const before = stepBefore(current);
  const after = stepAfter(current);
  return (
    <DraftsContext.Provider value={drafts.current}>
      <main className="cx-bootstrap">
        <h1>{TITLE}</h1>
        <p className="cx-bootstrap__muted">
          Tell Codex what happened before it existed. Each step shows a preview first and writes
          nothing until you press Apply; each applied step has its own Undo.
        </p>
        <section aria-label="Bootstrap" data-testid="bootstrap.page.root">
          <Stepper
            label="Bootstrap steps"
            testId="bootstrap.stepper"
            steps={STEP_IDS.map((id) => ({ id, label: STEP_LABELS[id], status: statuses[id] }))}
            onStepSelect={(id) => go(id as StepId)}
          />
          <h2 className="cx-bootstrap__step-title">{STEP_LABELS[current]}</h2>
          <Step />
          <footer className="cx-bootstrap__footer">
            <Button
              icon="arrow-left"
              data-testid="bootstrap.step.back"
              disabled={before === null}
              onClick={() => go(before)}
            >
              Back
            </Button>
            <Button
              variant="primary"
              iconEnd="arrow-right"
              data-testid="bootstrap.step.next"
              disabled={after === null}
              onClick={() => go(after)}
            >
              Next
            </Button>
          </footer>
        </section>
      </main>
    </DraftsContext.Provider>
  );
}
